import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { playNotificationSound } from '../utils/notificationSound';
import styled, { keyframes } from 'styled-components';

const slideInRight = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* Компактный стиль как у уведомления сотрудника: справа вверху, зелёная карточка */
const Item = styled.div`
  background: #28a745;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${slideInRight} 0.3s ease-out;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: box-shadow 0.2s;
  border: 1px solid rgba(255,255,255,0.2);

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
`;

const Title = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const Text = styled.div`
  opacity: 0.95;
  font-size: 13px;
  margin-bottom: 8px;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ConfirmBtn = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: rgba(255,255,255,0.95);
  color: #28a745;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    background: #fff;
    transform: scale(1.02);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const GoBtn = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 2px solid rgba(255,255,255,0.9);
  background: transparent;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    background: rgba(255,255,255,0.2);
    transform: scale(1.02);
  }
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 2px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  color: #fff;
  cursor: pointer;
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(255,255,255,0.35);
  }
`;

function formatHours(minutes) {
  if (minutes == null) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (m === 0) return `${h} ч`;
  return `${h} ч ${m} мин`;
}

export default function WorktimeCompletionNotifications() {
  const { state } = useApp();
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState({});
  const playedForIds = useRef(new Set());
  const [confirmingId, setConfirmingId] = useState(null);

  const role = (state.user?.role || '').toString().toLowerCase();
  const isHrOrAdmin = state.user && ['hr', 'admin', 'руководитель'].includes(role);

  const fetchUsers = useCallback(() => {
    if (!isHrOrAdmin) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(usersData => {
        const arr = Array.isArray(usersData) ? usersData : (Array.isArray(usersData?.users) ? usersData.users : []);
        setUsers(Object.fromEntries((arr || []).map(u => [u.id, u])));
      })
      .catch(() => {});
  }, [isHrOrAdmin]);

  useEffect(() => {
    if (!isHrOrAdmin) return;
    fetchUsers();
  }, [isHrOrAdmin, fetchUsers]);

  const socketCleanupRef = useRef(() => {});

  useEffect(() => {
    if (!isHrOrAdmin) return;

    const onCompletion = (data) => {
      const id = `worktime-${data.userId}-${data.date}-${Date.now()}`;
      setNotifications(prev => [...prev, { id, ...data }]);
      if (!playedForIds.current.has(id)) {
        playedForIds.current.add(id);
        playNotificationSound();
      }
    };

    const socket = typeof window !== 'undefined' ? window.socket : null;

    if (socket) {
      socket.on('worktime_completion_notification', onCompletion);
      socketCleanupRef.current = () => socket.off('worktime_completion_notification', onCompletion);
      return () => socketCleanupRef.current();
    }

    const intervalId = setInterval(() => {
      const s = typeof window !== 'undefined' ? window.socket : null;
      if (s) {
        clearInterval(intervalId);
        s.on('worktime_completion_notification', onCompletion);
        socketCleanupRef.current = () => s.off('worktime_completion_notification', onCompletion);
      }
    }, 500);
    return () => {
      clearInterval(intervalId);
      socketCleanupRef.current();
    };
  }, [isHrOrAdmin]);

  const handleDismiss = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleConfirm = async (n) => {
    const leaveId = n.leaveId;
    if (!leaveId) return;
    setConfirmingId(n.id);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/leaves/update-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ leaveId, status: 'completed' })
      });
      if (res.ok) {
        handleDismiss(n.id);
      }
    } finally {
      setConfirmingId(null);
    }
  };

  const handleGoToModal = (n) => {
    handleDismiss(n.id);
    window.dispatchEvent(new CustomEvent('show-leaves-worktime', { detail: { filter: 'completed' } }));
  };

  if (!isHrOrAdmin || notifications.length === 0) return null;

  return (
    <Container>
      {notifications.map(n => {
        const user = users[n.userId];
        const name = n.username || user?.username || (user?.first_name || user?.last_name
          ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
          : null) || `Сотрудник #${n.userId}`;

        return (
          <Item key={n.id}>
            <Body>
              <Title>Сотрудник отработал время</Title>
              <Text>
                {name}. Отработано: {formatHours(n.totalWorkedMinutes)} из {formatHours(n.requiredMinutes)}. Нужно подтвердить.
              </Text>
              <ActionsRow>
                <ConfirmBtn
                  type="button"
                  disabled={confirmingId === n.id || !n.leaveId}
                  onClick={(e) => { e.stopPropagation(); handleConfirm(n); }}
                  title="Подтвердить отработку (статус станет «Отработано»)"
                >
                  {confirmingId === n.id ? '…' : 'Подтвердить'}
                </ConfirmBtn>
                <GoBtn
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleGoToModal(n); }}
                  title="Открыть модалку отработки отгулов → Отработанные"
                >
                  Перейти
                </GoBtn>
              </ActionsRow>
            </Body>
            <CloseBtn
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDismiss(n.id); }}
              aria-label="Закрыть"
              title="Закрыть"
            >
              ×
            </CloseBtn>
          </Item>
        );
      })}
    </Container>
  );
}
